import React, { useEffect, useState } from 'react';
import CommonModal from './CommonModal';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { getSessionId } from '../../utils/sessionId';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { deletedLabel, isDeletedItem } from '../../utils/deletedItem';

// Subtle highlight for the per-item "Added by" label - a light amber tint (matching the assigned-
// table badge already used on the reservations calendar) rather than a loud color, just enough to
// draw the eye without competing with the product name above it.
const addedByBadgeStyle = {
  fontSize: '11px',
  display: 'inline-block',
  marginTop: 4,
  padding: '2px 6px',
  borderRadius: 4,
  background: 'rgba(255, 193, 7, 0.15)',
  color: '#ffc107',
};

// A deleted item stays on the order (the server soft-deletes it once the order was sent to the
// kitchen), so it is listed - but clearly marked, dimmed, and never selectable, so it cannot be
// mistaken for a live item or repeated back onto the table by "Add to Order".
const deletedRowStyle = { opacity: 0.55 };
const deletedNameStyle = { textDecoration: 'line-through' };
const deletedBadgeStyle = {
  fontSize: '11px',
  display: 'inline-block',
  marginTop: 4,
  marginLeft: 6,
  padding: '2px 6px',
  borderRadius: 4,
  background: 'rgba(220, 53, 69, 0.18)',
  color: '#ff6b6b',
};

export const FinalizeOrderCartItemModal = ({ isOpen, onClose, tableId, tableName }) => {
  const [items, setItems] = useState([]);
  const [deals, setDeals] = useState([]);
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tableId || !isOpen) return;

    async function load() {
      const res = await callAPI(`${apiUrls.GET_CART_ITEMS_BY_TABLE}?table_id=${tableId}`, 'GET');
      setItems(res.data.data.items);
      setDeals(res.data.data.data_deals);
    }
    load();
  }, [tableId, isOpen]);

  const toggleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Items/deals may come from more than one order (separate kitchen rounds still occupying
  // the same table) — group them by order_id so each round shows who took it. Within one order,
  // items are further attributed individually (server_name per row, from added_by_employee_id,
  // always shown per item regardless of whether one or several staff worked that order) — the
  // group header additionally lists every distinct name that shows up across the whole order.
  const buildOrderGroups = (deals, items) => {
    const groups = {};
    const groupKeys = [];

    const addRow = (orderId, row) => {
      const key = orderId ?? 'unknown';
      if (!groups[key]) {
        groups[key] = { orderId, serverNames: [], rows: [] };
        groupKeys.push(key);
      }
      const name = row.item.server_name || null;
      if (name && !groups[key].serverNames.includes(name)) {
        groups[key].serverNames.push(name);
      }
      groups[key].rows.push(row);
    };

    deals.forEach(item => addRow(item.order_id, { type: 'deal', item }));
    items.forEach(item => addRow(item.order_id, { type: 'item', item }));

    return groupKeys.map(key => groups[key]);
  };

  const repeatOrder = async () => {

    if (!selected || selected.length === 0) {
      toast.warning("Please select at least one item");
      return; 
    }
    const form = new FormData();
    form.append("cart_ids", JSON.stringify(selected));
    form.append("table_id", tableId);
    form.append("session_id", getSessionId());
  
    const res = await callAPI(apiUrls.REPEAT_ITEMS, {}, "POST", form, {});
  
    if (res.data.status === 1) {
      const data = res.data;

      navigate("/order-review", {
        state: {
          orderType: "dinein",
          selectedTables: [tableId],
  
          // pass additional values to OrderReview
          existing_order_id: data.existing_order_id,
          selected_customer_id: data.selected_customer_id,
          selected_table_ids: data.selected_table_ids
        }
      });
    }
  };
  

  return (
    <CommonModal isOpen={isOpen} onClose={onClose}>
      <div className="modal-header">
        <h4>Table {tableName} &middot; Items</h4>
        <button className="btn-close" onClick={onClose}></button>
      </div>

      <div className="modal-body p-0">
        <table className="table">
          <thead>
            <tr>
              <th></th><th></th><th>Product</th><th>Price</th><th>Qty</th><th>Total</th>
            </tr>
          </thead>

          <tbody>
            {buildOrderGroups(deals, items).map(group => (
              <React.Fragment key={group.orderId ?? 'unknown'}>
                <tr>
                  <td colSpan={6} style={{ background: 'rgba(255,255,255,0.06)', fontWeight: 600 }}>
                    {group.orderId ? `Order #${group.orderId}` : 'Order'}
                    <span style={{ fontWeight: 400, opacity: 0.75, marginLeft: 10 }}>
                      {group.serverNames.length > 1 ? 'Servers' : 'Server'}: {group.serverNames.join(', ') || 'Unassigned'}
                    </span>
                  </td>
                </tr>
                {group.rows.map(({ type, item }) => (
                  type === 'deal' ? (
                    <tr key={`deal-${item.cid}`} style={isDeletedItem(item) ? deletedRowStyle : undefined}>
                      <td><input type="checkbox" disabled={isDeletedItem(item)} onChange={() => toggleSelect(item.cid)} /></td>
                      <td><img src={item.product_thumbnail} width="50" /></td>
                      <td>
                        <span className="d-block" style={isDeletedItem(item) ? deletedNameStyle : undefined}>{item.item_name}</span>
                        {item.server_name && (
                          <span style={addedByBadgeStyle}>Added by: {item.server_name}</span>
                        )}
                        {isDeletedItem(item) && (
                          <span style={deletedBadgeStyle}>{deletedLabel(item.item_deleted)}</span>
                        )}
                      </td>
                      <td>{item.per_item_price}</td>
                      <td>{item.item_quantity}</td>
                      <td>{item.total_price}</td>
                    </tr>
                  ) : (
                    <tr key={`item-${item.item_id}`} style={isDeletedItem(item) ? deletedRowStyle : undefined}>
                      <td><input type="checkbox" disabled={isDeletedItem(item)} onChange={() => toggleSelect(item.item_id)} /></td>
                      <td><img src={item.product_thumbnail} width="50" /></td>
                      <td>
                        <span className="proName d-block" style={isDeletedItem(item) ? deletedNameStyle : undefined}>{item.product_name}</span>
                      {item.product_detail? (
                          <span className = "proDetail" dangerouslySetInnerHTML={{ __html: item.product_detail }} />
                        ) : (
                          <span></span>
                        )}
                        {item.server_name && (
                          <span style={addedByBadgeStyle}>Added by: {item.server_name}</span>
                        )}
                        {isDeletedItem(item) && (
                          <span style={deletedBadgeStyle}>{deletedLabel(item.item_deleted)}</span>
                        )}
                      </td>
                      <td>{item.per_item_price}</td>
                      <td>{item.quantity}</td>
                      <td>{item.total_price}</td>
                    </tr>
                  )
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="modal-footer justify-content-between">
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={repeatOrder}>Add to Order</button>
      </div>
    </CommonModal>
  );
};
